import React, {useEffect, useRef, useState} from 'react';
import {Input} from "../../fusion-ui";
import {useTheme} from "../../ProjectThemeProvider";
import {checkNullStr, isBoolTrue, parseInteger} from "../../fusion-ui/utils/ReactUtils";

/**
 * Create a theme based input component.
 *
 * @example
 *      const [emailInputValue, setEmailInputValue] = useState('');
 *      const [emailInputMsg, setEmailInputMsg] = useState('');
 *
 *      <ThemedInput
 *           mainWrapperClassName={`w-100 pb-2`}
 *           className={`py-2`}
 *           labelClassName={``}
 *           style={{height: '36px'}}
 *           type={'email'}
 *           id={'email-field'}
 *           label={'Email'}
 *           placeholder={'Enter your email'}
 *           value={emailInputValue}
 *           required={true}
 *           validate={true}
 *           pattern={/^[^\s@]+@[^\s@]+\.[^\s@]+$/}
 *           msg={emailInputMsg}
 *           {...rest}
 *           onChange={(e, isValid) => {
 *               const value = e.target.value;
 *               setEmailInputValue(value);
 *               setEmailInputMsg(!isValid?'Invalid email':'');
 *           }}
 *      />
 */
const ThemedInput = React.memo(({
                                    mainWrapperClassName = "",
                                    wrapperClassName = "",
                                    className = "",
                                    children,
                                    style = {},
                                    labelStyle = {},
                                    labelClassName = "",
                                    onChange,
                                    onBlur,
                                    id,
                                    label,
                                    labelColor,
                                    placeholder,
                                    type,
                                    value,
                                    defaultValue,
                                    msg,
                                    required,
                                    validate,
                                    maxLength,
                                    asTextArea,
                                    pattern,
                                    showMsg,
                                    readOnly,
                                    disabled,
                                    showDefMsg = true,
                                    showDefMsgOverride = true,
                                    showPasswordToggle = true,
                                    showLimitLabel = false,
                                    variant,
                                    iMsg,
                                    ...rest
                                }) => {
    const TAG = "ThemedInput";
    const INPUT_CLS = 'DFGU456&UHVD456HDFGHNBVDW34JFR6BVCDSW2QASDFGH';

    const theme = useTheme();

    const [isTouched, setIsTouched] = useState(false);

    // Create a ref to the element
    const myElementRef = useRef(null);

    // State to track whether the element is hovered, focused, or active
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isActive, setIsActive] = useState(false);

    // Effect to attach event listeners when the component mounts
    useEffect(() => {
        const handleMouseEnter = () => setIsHovered(true);
        const handleMouseLeave = () => setIsHovered(false);
        const handleFocus = () => setIsFocused(true);
        const handleBlur = () => setIsFocused(false);
        const handleMouseDown = () => setIsActive(true);
        const handleMouseUp = () => setIsActive(false);

        try {
            const element = myElementRef.current;

            // Attach event listeners
            element.addEventListener('mouseenter', handleMouseEnter);
            element.addEventListener('mouseleave', handleMouseLeave);
            // element.addEventListener('focus', handleFocus);
            // element.addEventListener('blur', handleBlur);
            // element.addEventListener('mousedown', handleFocus);
            // element.addEventListener('mouseup', handleUnfocus);

            // Cleanup event listeners when the component unmounts
            return () => {
                // element.removeEventListener('mouseenter', handleMouseEnter);
                // element.removeEventListener('mouseleave', handleMouseLeave);
                // element.removeEventListener('focus', handleFocus);
                // element.removeEventListener('blur', handleBlur);
                // element.removeEventListener('mousedown', handleMouseDown);
                // element.removeEventListener('mouseup', handleMouseUp);
            };

        } catch (error) {
            console.error(error);
        }
    }, []); // Empty dependency array ensures the effect runs only once


    const handleFocus = () => {
        setIsTouched(true);
    };

    const handleBlur = (e) => {
        setIsTouched(false);
        if (onBlur) onBlur(e);
    };

    const handleUnfocus = () => {
        setIsTouched(false);
    };

    const inputStyles = (isTouched || isHovered || isFocused || isActive) ? {
        borderColor: isBoolTrue(readOnly) ? '' : theme.primary,
        color: isBoolTrue(readOnly) ? '' : theme.primary,
        // boxShadow: `0 0 0.05rem 0.1rem ${theme.primary20}`,
        backgroundColor: isBoolTrue(readOnly) ? theme.text05 : '',
    } : {
        backgroundColor: isBoolTrue(readOnly) ? theme.text05 : '',
    };

    return (<div className={`position-relative bg-infox ${mainWrapperClassName}`} ref={myElementRef}>
        <Input
            wrapperClassName={`w-100 ${wrapperClassName}`}
            className={`w-100 ${INPUT_CLS} ${className} shadow-none`}
            labelClassName={labelClassName}
            labelStyle={{color: theme.text40, ...labelStyle}}
            type={type}
            // labelColor={getDefStr(labelColor,theme.text40)}
            pattern={pattern}
            variant={null}
            iMsg={iMsg}
            name={type}
            id={id}
            placeholderAsLabel={false}
            placeholder={placeholder}
            // label={getDefStr(label, placeholder)}
            label={label}
            value={value}
            defaultValue={defaultValue}
            required={checkNullStr(label) && isBoolTrue(required)}
            msg={msg}
            maxLength={getMaxLen()}
            validate={validate}
            showMsgOverride={showMsg}
            showMsg={showMsg}
            showDefMsg={showDefMsg}
            showDefMsgOverride={showDefMsgOverride}
            asTextArea={asTextArea}
            readOnly={readOnly}
            showPasswordToggle={showPasswordToggle}
            disabled={disabled}
            italicMsg={false}
            onBlur={handleBlur}
            onMouseDown={handleFocus}
            onMouseUp={handleUnfocus}
            onMouseEnter={handleFocus}
            onFocus={handleFocus}
            onChange={(e, valid) => {
                if (parseInteger(getMaxLen(), -1) > 0) {
                    if ((e.target.value + '').length <= parseInteger(getMaxLen())) {
                        if (onChange) onChange(e, valid);
                    }
                } else {
                    if (onChange) onChange(e, valid);
                }
                // if (onChange) onChange(e, valid);
                handleFocus();
            }}
            onMouseLeave={() => {
                handleUnfocus();
            }}
            style={{...style, ...inputStyles}}

            // onMouseEnter={handleFocus}
            // onMouseLeave={handleUnfocus}
            // onClick={handleFocus}
            // onDragEnter={handleFocus}
            // onDragLeave={handleUnfocus}
            // onDoubleClick={handleFocus}
            {...rest}
        />
        {isBoolTrue(showLimitLabel) &&
            <label className={'fs-md-sm fw-semi-bold position-absolute'}
                   style={{
                       bottom: '-.15rem',
                       right: '.2rem',
                       color: theme.text40
                   }}>
                {(value + '').length}{checkNullStr(getMaxLen()) ? `/${getMaxLen()}` : ''}
            </label>}
    </div>);

    function getMaxLen() {
        if (checkNullStr(maxLength) && parseInteger(maxLength) > 0) {
            return isBoolTrue(asTextArea) ? maxLength * 4 : parseInteger(maxLength);
        } else {
            return '';
        }
    }
})

export default ThemedInput;
